import { createTheme as createMuiTheme } from "@mui/material/styles";
import { CONDENSED_FONT } from "./condensed-fonts";
import { HYPHENATION_SETTINGS } from "./hyphenation";
export const createTheme = () => {
    const theme = createMuiTheme({
        textUnderlineOffset: "0.175em",
        breakpoints: {
            values: {
                xs: 0,
                sm: 720,
                md: 1140,
                lg: 1300,
                xl: 1536,
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: `
        a {
          color: inherit;
          text-decoration: none;
        }

        ul.list,
        ol {
          padding-left: 0;
          list-style-position: inside;
        }

        ul.list li {
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='5' viewBox='0 0 5 5'%3E%3Cpath d='M105.249,224.355a4.859,4.859,0,1,0-.452-2.047A2.914,2.914,0,0,0,105.249,224.355Z' transform='translate(-104.797 -219.808)' fill='%23fff'/%3E%3C/svg%3E%0A") no-repeat left 0.575em;
          padding: 0px 10px 0px 1em;
          list-style: none;
          margin: 0;
          vertical-align: middle;
          padding-bottom: 4px;
        }
      `,
            },
        },
        palette: {
            background: { default: "#f94d1b", paper: "#e23300" },
            primary: { main: "#6e0e14" },
            secondary: { main: "#ffffff" },
            text: { primary: "#ffffff" },
        },
        shape: { borderRadius: 2.2361 },
        spacing: (factor) => `${0.5 * factor}rem`,
        typography: {
            fontFamily: [
                "eurostile",
                "-apple-system",
                "BlinkMacSystemFont",
                '"Segoe UI"',
                "Roboto",
                '"Helvetica Neue"',
                "Arial",
                "sans-serif",
            ].join(","),
            htmlFontSize: 16,
            fontSize: 24,
            fontWeightBold: 800,
        },
    });
    /**
     * Typography
     */
    theme.typography.h1 = {
        ...theme.typography.h1,
        ...HYPHENATION_SETTINGS,
        fontFamily: CONDENSED_FONT,
        fontSize: "3.25rem",
        fontWeight: 800,
        letterSpacing: "0",
        lineHeight: 1,
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: theme.spacing(2),
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
            fontFamily: theme.typography.fontFamily,
            fontSize: "3.375rem",
            fontWeight: 900,
            letterSpacing: "0.02em",
            lineHeight: 1,
            paddingBottom: theme.spacing(4),
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "3.75rem", // 60px
        },
    };
    theme.typography.h2 = {
        ...theme.typography.h2,
        ...HYPHENATION_SETTINGS,
        fontFamily: CONDENSED_FONT,
        fontSize: "2.25rem",
        fontWeight: 800,
        letterSpacing: "0",
        lineHeight: 1.11111,
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(6),
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
            fontFamily: theme.typography.fontFamily,
            fontSize: "2.25rem",
            fontWeight: 800,
            letterSpacing: "0.04em",
            lineHeight: 1.2,
        },
        [theme.breakpoints.up("md")]: {
            fontSize: "2.375rem", // 38px
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "2.5rem", // 40px
        },
    };
    theme.typography.h3 = {
        ...theme.typography.h3,
        ...HYPHENATION_SETTINGS,
        fontFamily: CONDENSED_FONT,
        fontSize: "2rem",
        fontWeight: 800,
        letterSpacing: "0.04rem",
        lineHeight: 1.125,
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(4),
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
            fontFamily: theme.typography.fontFamily,
            fontSize: "2rem",
            fontWeight: 800,
            letterSpacing: "0.03em",
            lineHeight: 1.2,
        },
        [theme.breakpoints.up("md")]: {
            fontSize: "2.125rem", // 34px
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "2.25rem", // 36px
        },
    };
    theme.typography.h4 = {
        ...theme.typography.h4,
        ...HYPHENATION_SETTINGS,
        fontFamily: CONDENSED_FONT,
        fontSize: "1.75rem",
        fontWeight: 900,
        letterSpacing: "0.03em",
        lineHeight: 1.142857142857143,
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(3),
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
            fontFamily: theme.typography.fontFamily,
            fontSize: "1.5rem",
            fontWeight: 900,
            lineHeight: 1.3,
        },
        [theme.breakpoints.up("md")]: {
            fontSize: "1.625rem", // 26 px
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "1.75rem", // 28px
        },
        borderRadius: {
            none: "0px",
            DEFAULT: "5px",
        },
    };
    theme.typography.h5 = {
        ...theme.typography.h5,
        ...HYPHENATION_SETTINGS,
        fontFamily: theme.typography.fontFamily,
        fontSize: "1.5rem",
        fontWeight: 800,
        letterSpacing: "0.04em",
        lineHeight: 1.166666666666667,
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(3),
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
            fontFamily: theme.typography.fontFamily,
            fontSize: "1.5rem",
            fontWeight: 500,
            lineHeight: 1.3,
        },
        [theme.breakpoints.up("md")]: {
            fontSize: "1.625rem", // 26 px
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "1.75rem", // 28px
        },
    };
    theme.typography.h6 = {
        ...theme.typography.h6,
        ...HYPHENATION_SETTINGS,
        fontFamily: theme.typography.fontFamily,
        fontSize: "1.25rem",
        fontWeight: 400,
        letterSpacing: "0.02em",
        lineHeight: 1.2,
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: 0,
        paddingTop: theme.spacing(4),
        [theme.breakpoints.up("md")]: {
            hyphens: "none",
            fontSize: "1.375rem",
            lineHeight: 1.333333333333333,
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "1.5rem", // 24px
        },
    };
    theme.typography.overline = {
        ...theme.typography.overline,
        display: "inline-block",
        fontSize: "1.5rem",
        fontWeight: 400,
        letterSpacing: "0.015em",
        lineHeight: 1.1,
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: theme.spacing(1.5),
        paddingTop: 0,
        textTransform: "none",
        [theme.breakpoints.up("sm")]: {
            fontSize: "1.85rem",
            lineHeight: 1,
        },
    };
    theme.typography.body1 = {
        ...theme.typography.body1,
        ...HYPHENATION_SETTINGS,
        fontFamily: theme.typography.fontFamily,
        fontSize: "1.25rem",
        fontWeight: 400,
        letterSpacing: "0.02em",
        lineHeight: 1.2,
        [theme.breakpoints.up("md")]: {
            fontSize: "1.375rem",
            lineHeight: 1.333333333333333,
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "1.5rem", // 24px
        },
    };
    theme.typography.body2 = {
        ...theme.typography.body1,
        [theme.breakpoints.up("xl")]: {
            ...theme.typography.body2,
            ...HYPHENATION_SETTINGS,
            fontSize: "1.75rem",
            fontWeight: 500,
            letterSpacing: "0.07rem",
            lineHeight: 1.428571428571429,
        },
    };
    theme.typography.caption = {
        ...theme.typography.caption,
        fontFamily: CONDENSED_FONT,
        fontSize: "1rem",
        fontWeight: 400,
        letterSpacing: "0.02em",
        lineHeight: "1.25rem",
        [theme.breakpoints.up("sm")]: {
            fontFamily: CONDENSED_FONT,
            fontSize: "1.25rem",
            fontWeight: 400,
            lineHeight: 1.2,
        },
    };
    theme.shadows = {
        ...theme.shadows,
        [0]: "0px 0px 0px 0px rgba(0,0,0,0),0px 0px 0px 0px rgba(0,0,0,0),0px 0px 0px 0px rgba(0,0,0,0)",
        [4]: "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
    };
    /**
     * Buttons
     */
    theme.components = {
        ...theme.components,
        MuiIconButton: {
            styleOverrides: {
                colorSecondary: {
                    "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                    },
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: "5px",
                    fontWeight: 400,
                    fontSize: "1.25rem",
                    lineHeight: "1.5rem",
                    letterSpacing: "0.02em",
                    padding: `${theme.spacing(1.5)} ${theme.spacing(3)} ${theme.spacing(1.75)} `,
                    textTransform: "none",
                    [theme.breakpoints.up("xl")]: {
                        letterSpacing: "0.03rem",
                        fontSize: "1.5rem",
                        lineHeight: 1.333333333333333,
                        padding: `${theme.spacing(2)} ${theme.spacing(4)} ${theme.spacing(2.25)}`,
                    },
                },
                outlined: {
                    borderWidth: "2px",
                    "&:hover": {
                        borderWidth: "2px",
                    },
                },
            },
        },
    };
    /**
     * Container
     */
    theme.components.MuiContainer = {
        defaultProps: {
            sx: {
                maxWidth: { xs: "md", xl: "lg" },
                overflowWrap: "break-word",
                position: "relative",
                zIndex: 5,
            },
        },
    };
    /**
     * TableCell
     */
    theme.components.MuiTableCell = {
        styleOverrides: {
            root: { paddingLeft: 0, paddingRight: 0 },
        },
    };
    /**
     * Tooltip
     */
    theme.components.MuiTooltip = {
        styleOverrides: {
            arrow: { "&:before": { backgroundColor: "white" } },
            tooltip: { backgroundColor: "white", color: theme.palette.primary.dark },
        },
    };
    /**
     * TextField
     */
    theme.components.MuiOutlinedInput = {
        styleOverrides: {
            notchedOutline: {
                borderColor: "white",
                borderRadius: "5px",
                borderWidth: 2,
                opacity: 0.75,
                transition: theme.transitions.create("opacity"),
            },
            root: {
                borderRadius: theme.shape.borderRadius,
                "&:hover": { ".MuiOutlinedInput-notchedOutline": { opacity: "1 !important" } },
                ".MuiNativeSelect-iconOutlined": {
                    color: "white",
                    height: "0.85em",
                    right: "2px",
                    top: "calc(50% - .4em)",
                    width: "0.85em",
                },
            },
        },
    };
    /**
     * Paper
     */
    theme.components.MuiPaper = {
        defaultProps: {
            elevation: 0,
        },
    };
    /**
     * Form inputs
     */
    theme.components.MuiFormHelperText = {
        styleOverrides: {
            root: {
                color: "white",
            },
        },
    };
    theme.components.MuiInputLabel = {
        styleOverrides: {
            root: {
                color: "white !important",
            },
            error: {
                color: "white !important",
            },
            focused: {
                color: "white !important",
            },
            shrink: {
                color: "white !important",
            },
        },
    };
    theme.components.MuiFilledInput = {
        styleOverrides: {
            root: {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: theme.palette.primary.main,
                ".MuiInputAdornment-positionEnd": {
                    color: "white",
                },
                "&.Mui-focused": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                },
                "&.Mui-error": {
                    color: "white",
                    "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        "&:after": {
                            borderBottom: "2px solid rgba(255, 255, 255, 0.6) !important",
                            borderBottomColor: "rgba(255, 255, 255, 0.6)",
                        },
                    },
                    "&:after": {
                        borderBottom: "2px solid rgba(255, 255, 255, 0.4)",
                        borderBottomColor: "rgba(255, 255, 255, 0.6)",
                    },
                },
                "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    "&:before": {
                        borderBottom: "2px solid rgba(255, 255, 255, 0.6) !important",
                    },
                    "&:after": {
                        borderBottom: "2px solid rgba(255, 255, 255, 0.6) !important",
                    },
                },
                "&:focused": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    "&:before": {
                        borderBottom: "2px solid rgba(255, 255, 255, 0.6) !important",
                    },
                    "&:after": {
                        borderBottom: "2px solid rgba(255, 255, 255, 0.6) !important",
                    },
                },
                "&:before": {
                    borderBottom: "2px solid rgba(255, 255, 255, 0.4)",
                },
                "&:after": {
                    borderBottom: "2px solid rgba(255, 255, 255, 0.4)",
                },
            },
            input: {
                color: "white",
                fontWeight: 600,
                letterSpacing: "0.05em",
            },
        },
    };
    return theme;
};
//# sourceMappingURL=create-theme.js.map